import React from 'react'
import { DateFns, YearMonthFns } from '../utils/datetime'
import { OverrideComponentProps } from '../utils/overridable-component-helper'
import { DateRangePickerContext } from './date-range-picker-root'
import { composeEventHandlers } from '../utils/compose-event-handlers'
import { composeRefs } from '../utils/compose-refs'
import { DateRangePickerMonthContext } from './date-range-picker-month'

export type DateRangePickerDayProps<Tag extends React.ElementType<any>> = OverrideComponentProps<
  Tag,
  {
    day: number
    dayOfWeek: number
    date: string
    isCurrentMonth: boolean
    isLastDayOfMonth: boolean
    isToday: boolean
    isStart: boolean
    isEnd: boolean
    inRange: boolean
    preview: {
      isStart: boolean
      isEnd: boolean
      inRange: boolean
    }
    disabled: boolean
    selectDisabled: boolean
  }
> & { date: string }

export const DateRangePickerDay = React.forwardRef<HTMLDivElement, DateRangePickerDayProps<any>>(
  ({ date, as = 'button', className, children, ...props }, forwardRef) => {
    const context = React.useContext(DateRangePickerContext)
    const monthContext = React.useContext(DateRangePickerMonthContext)
    if (context && monthContext) {
      const ref = React.useRef<any>(null)

      const isCurrentMonth = YearMonthFns.getMonthNumber(monthContext.yearMonth) === DateFns.getMonth(date)

      const isDisabled = React.useMemo(
        () => isCurrentMonth && context.shouldDisableDate(date),
        [context, date, isCurrentMonth]
      )
      const isDisabledSelect = React.useMemo(
        () =>
          isCurrentMonth &&
          (isDisabled ||
            (context.currentField === 'start' && context.shouldDisableStartDate(date, context.end)) ||
            (context.currentField === 'end' && context.shouldDisableEndDate(date, context.start))),
        [context, date, isCurrentMonth, isDisabled]
      )

      const onMouseEnter = React.useCallback(() => {
        if (isCurrentMonth) {
          context.setMouseOver(date)
        }
      }, [context, isCurrentMonth, date])

      const slots = React.useMemo(() => {
        const isStart = isCurrentMonth && date === context.start
        const isEnd = isCurrentMonth && date === context.end
        const inRange =
          isCurrentMonth &&
          (!!(
            context.start &&
            context.end &&
            DateFns.isEqualOrBefore(context.start, date) &&
            DateFns.isEqualOrAfter(context.end, date)
          ) ||
            date === context.start)

        const isPreviewStart = isCurrentMonth && date === context.selectionPreview?.start
        const isPreviewEnd = isCurrentMonth && date === context.selectionPreview?.end
        const inPreviewRange =
          isCurrentMonth &&
          !!(
            context.selectionPreview?.start &&
            context.selectionPreview?.end &&
            DateFns.isEqualOrBefore(context.selectionPreview.start, date) &&
            DateFns.isEqualOrAfter(context.selectionPreview.end, date)
          )
        return {
          date,
          day: DateFns.getDate(date),
          isCurrentMonth: isCurrentMonth,
          isToday: DateFns.toISODate(new Date()) === date,
          isStart,
          isEnd,
          inRange,
          preview: {
            isStart: isPreviewStart,
            isEnd: isPreviewEnd,
            inRange: inPreviewRange
          },
          dayOfWeek: DateFns.getDayOfWeek(date),
          isLastDayOfMonth: isCurrentMonth && DateFns.getDate(date) === YearMonthFns.getDaysInMonth(monthContext.yearMonth),
          disabled: isDisabled,
          selectDisabled: isDisabledSelect
        }
      }, [
        date,
        monthContext.yearMonth,
        context.selectionPreview,
        context.start,
        context.end,
        isCurrentMonth,
        isDisabled,
        isDisabledSelect
      ])
      return React.createElement(
        as,
        {
          ...props,
          ref: composeRefs(forwardRef, ref),
          onMouseEnter: composeEventHandlers(props.onMouseEnter, onMouseEnter),
          onClick: composeEventHandlers(
            (e: React.MouseEvent<any>) => {
              if (isDisabledSelect) {
                e.preventDefault()
                e.stopPropagation()
              } else {
                context.onSelect(date)
              }
            },
            props.onClick,
            {
              checkForDefaultPrevented: true
            }
          ),
          className: typeof className === 'function' ? className(slots) : className
        },
        ...React.Children.toArray(typeof children === 'function' ? children(slots) : children)
      )
    } else {
      throw new Error('Cannot find date picker context')
    }
  }
) as WithForwardRefType

DateRangePickerDay.displayName = 'DateRangePickerDay'

interface WithForwardRefType extends React.FC<DateRangePickerDayProps<React.ElementType<any>>> {
  <T extends React.ElementType<any> = 'button'>(
    props: DateRangePickerDayProps<T>
  ): ReturnType<React.FC<DateRangePickerDayProps<T>>>
}
